import React, { useState } from 'react';
import { styled } from '@compiled/react';
import {
	graphql,
	usePreloadedQuery,
	type PreloadedQuery,
	useQueryLoader,
	commitLocalUpdate,
	useRelayEnvironment,
	ConnectionHandler,
} from 'react-relay';
import Button, { ButtonGroup } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { JiraVersionAri } from '@atlassian/ari/jira/version';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import ProjectSettingsBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project-settings/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { NotFoundErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import createReleaseModalQuery, {
	type createReleaseModalQuery as createReleaseModalQueryType,
} from '@atlassian/jira-relay/src/__generated__/createReleaseModalQuery.graphql';
import type { uiProjectVersionsQuery } from '@atlassian/jira-relay/src/__generated__/uiProjectVersionsQuery.graphql';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import AsyncReleaseAutomationMenu from '@atlassian/jira-software-releases-automation-menu/src/async.tsx';
import { useCreateReleaseExperienceStart } from '@atlassian/jira-software-releases-release-modals-relay/src/services/create-release-experience-tracker/index.tsx';
import { CreateReleaseModal } from '@atlassian/jira-software-releases-release-modals-relay/src/ui/create-release-modal/async.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { LazySuspense } from '@atlassian/react-loosely-lazy';
import { useResource } from '@atlassian/react-resource-router';
import RelayDataID from '@atlassian/relay-data-id';
import { PROJECT_VERSIONS_ANALYTICS_SOURCE } from '../common/constants.tsx';
import type { Version } from '../common/types.tsx';
import { ProjectVersionsAnalyticsContextProvider } from '../services/analytics/ProjectVersionsAnalyticsContextProvider.tsx';
import {
	JiraProjectVersionExperienceExpectedFailureSuccess,
	JiraProjectVersionExperienceSuccess,
} from '../services/experience-tracker/JiraProjectVersionExperienceExpectedFailureSuccess.tsx';
import { PerformanceTracker } from '../services/performance-tracker/PerformanceTracker.tsx';
import { FeedbackButton } from './feedback/FeedbackButton.tsx';
import StatusFilter from './filter/StatusFilter.tsx';
import messages from './messages.tsx';
import Search from './search/index.tsx';
import Table from './table/index.tsx';
import { getProjectType, isVersionAdminContext } from './utils.tsx';

export type Props = {
	queryReference: PreloadedQuery<uiProjectVersionsQuery>;
};

export const ProjectVersionsComponent = ({ queryReference }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();

	/* eslint-disable @atlassian/relay/unused-fields */
	const data = usePreloadedQuery(
		graphql`
			query uiProjectVersionsQuery(
				$initialLoadCount: Int!
				$filter: [JiraVersionStatus]!
				$searchString: String!
				$sortBy: JiraVersionSortInput!
				$cloudId: ID!
				$projectKey: String!
				$isManageVersionsPermissionEnabled: Boolean!
			) {
				jira @required(action: THROW) {
					jiraProjectByKey(key: $projectKey, cloudId: $cloudId) @required(action: THROW) {
						projectId @required(action: THROW)
						name @required(action: THROW)
						key @required(action: THROW)
						jiraProjectType: projectType
						canEditVersions: action(type: MANAGE_VERSIONS)
							@include(if: $isManageVersionsPermissionEnabled) {
							canPerform @required(action: THROW)
						}
						versions(
							first: $initialLoadCount
							filter: $filter
							searchString: $searchString
							sortBy: $sortBy
						) @connection(key: "uiProject__versions") @required(action: THROW) {
							__id
							edges {
								__typename
							}
							errors {
								extensions {
									statusCode
								}
							}
						}
					}
				}

				...table_projectVersions
					@arguments(
						first: $initialLoadCount
						filter: $filter
						searchString: $searchString
						sortBy: $sortBy
						isManageVersionsPermissionEnabled: $isManageVersionsPermissionEnabled
					)
			}
		`,
		queryReference,
	);

	const { projectId, name, key, jiraProjectType } = data.jira.jiraProjectByKey;
	const connectionId = data.jira.jiraProjectByKey.versions.__id;

	const projectType = getProjectType(jiraProjectType);
	const accountId = useAccountId();
	const cloudId = useCloudId();
	// remove this variable when we don't need delegated admin analytics anymore
	const isJiraAdmin = useIsAdmin();
	const activationId = useActivationId();
	const projectContext = useResource(projectContextResource);
	// remove this variable when we don't need delegated admin analytics anymore
	const isProjectAdmin = projectContext?.data?.isProjectAdmin || false;
	const canEditVersions =
		fg('vmc-11-manage-versions-permission') &&
		data.jira.jiraProjectByKey.canEditVersions?.canPerform;
	const environment = useRelayEnvironment();

	const [createReleaseFormQueryRef, loadQuery] =
		useQueryLoader<createReleaseModalQueryType>(createReleaseModalQuery);

	const loadCreateReleaseQuery = () => {
		if (!createReleaseFormQueryRef && accountId) {
			loadQuery({ accountId });
		}
	};

	const [createVersionModalOpen, setCreateVersionModalOpen] = useState(false);
	const createVersionExperienceStart = useCreateReleaseExperienceStart(
		PROJECT_VERSIONS_ANALYTICS_SOURCE,
	);

	const hasPermissionError = (data.jira.jiraProjectByKey.versions.errors ?? []).some((error) =>
		(error.extensions ?? []).some((extension) => extension.statusCode === 403),
	);
	// Due to this condition, inside any subtree can have `versions @required(action: THROW)`.
	if (hasPermissionError) {
		return (
			<>
				<NotFoundErrorPageAsync />
				<JiraProjectVersionExperienceExpectedFailureSuccess reason="User does not have permission to view this page." />
			</>
		);
	}

	const onCreateRelease = (version: Version): void => {
		commitLocalUpdate(environment, (store) => {
			const connectionRecord = store.get(connectionId);
			if (connectionRecord) {
				const versionAri = JiraVersionAri.create({
					siteId: cloudId,
					activationId,
					versionId: version.id.toString(),
				}).toString();

				const dataID = RelayDataID({ id: versionAri }, 'JiraVersion') ?? '';
				const versionRecord = store.get(dataID);

				if (versionRecord) {
					const newEdgeRecord = ConnectionHandler.createEdge(
						store,
						connectionRecord,
						versionRecord,
						'JiraVersionEdge',
					);
					ConnectionHandler.insertEdgeBefore(connectionRecord, newEdgeRecord);
				}
			}
		});
	};

	const breadcrumbs = <BreadcrumbRenderer />;

	const actions = (
		<ButtonGroup>
			{!getWillShowNav4() && (
				<JSErrorBoundary
					fallback="unmount"
					packageName="jiraProjectVersions"
					teamName="jolt"
					id="releasesAutomationMenu"
				>
					<AsyncReleaseAutomationMenu />
				</JSErrorBoundary>
			)}
			<FeedbackButton />
		</ButtonGroup>
	);

	const createVersionButton = (
		fg('vmc-11-manage-versions-permission') ? canEditVersions : isJiraAdmin || isProjectAdmin
	) ? (
		<Button
			appearance="primary"
			onClick={(_e, analyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'createVersionButton');
				setCreateVersionModalOpen(true);
				createVersionExperienceStart();
			}}
			onMouseOver={loadCreateReleaseQuery}
			onFocus={loadCreateReleaseQuery}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="project-directories.versions.main.add-version.create-button"
		>
			{formatMessage(messages.createVersionButton)}
		</Button>
	) : null;

	const createVersionModal =
		createVersionModalOpen && createReleaseFormQueryRef != null ? (
			<CreateReleaseModal
				queryReference={createReleaseFormQueryRef}
				onClose={() => setCreateVersionModalOpen(false)}
				projectId={projectId}
				onReleaseCreation={(version) => {
					onCreateRelease(version);
					showFlag({
						type: 'success',
						title: formatMessage(messages.successFlagDescription, {
							name: version.name,
						}),
						isAutoDismiss: true,
						testId: 'project-directories.versions.flags.create.success',
						messageId: 'project-versions.ui.show-flag.success',
						messageType: 'transactional',
					});
				}}
			/>
		) : null;

	const bottomBar = (
		<BottomBarWrapper>
			<Filter>
				<Search />
				<Box xcss={versionsFilterContainerStyles}>
					<StatusFilter />
				</Box>
			</Filter>
			{getWillShowNav4() && projectType === 'software' ? (
				<ButtonGroup>
					{actions}
					{createVersionButton}
					{createVersionModal}
				</ButtonGroup>
			) : (
				<>
					{createVersionButton}
					{createVersionModal}
				</>
			)}
		</BottomBarWrapper>
	);

	const pageContent = (
		<Box xcss={containerStyles}>
			<DocumentTitle title={formatMessage(messages.releases)} />
			{getWillShowNav4() && projectType === 'software' && !isVersionAdminContext() ? (
				<Box xcss={isVisualRefreshEnabled() ? headerBarStyles : headerBarStylesOld}>
					{bottomBar}
				</Box>
			) : (
				<PageHeader
					truncateTitle
					breadcrumbs={breadcrumbs}
					actions={!getWillShowNav4() && actions}
					bottomBar={bottomBar}
				>
					{formatMessage(messages.releases)}
				</PageHeader>
			)}
			<LazySuspense fallback={null}>
				<Table query={data} />
				<JiraProjectVersionExperienceSuccess />
				<PerformanceTracker />
				<FireScreenAnalytics />
			</LazySuspense>
		</Box>
	);

	return (
		<ProjectVersionsAnalyticsContextProvider
			canEditVersions={canEditVersions}
			isJiraAdmin={isJiraAdmin}
		>
			{/* This data-testId is used in Pollinators */}
			{/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */}
			<div data-testid="project-directories.versions.main.name">
				{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
				{/* @ts-ignore TS2741 */}
				<ProjectBreadcrumb projectKey={key} projectName={name}>
					{isVersionAdminContext() ? (
						<ProjectSettingsBreadcrumb projectKey={key} projectType={projectType}>
							{pageContent}
						</ProjectSettingsBreadcrumb>
					) : (
						pageContent
					)}
				</ProjectBreadcrumb>
			</div>
		</ProjectVersionsAnalyticsContextProvider>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Filter = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const versionsFilterContainerStyles = xcss({
	marginLeft: 'space.150',
});

const containerStyles = xcss({
	marginBottom: 'space.300',
});

const headerBarStyles = xcss({
	marginTop: 'space.250',
	marginBottom: 'space.200',
});

const headerBarStylesOld = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BottomBarWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});
